import { Card, CardHeader, CardTitle, CardContent, CardFooter } from "@/components/ui/card";
import { WA_TOOL_COMMANDS } from '@bt-common/interfaces/whatsapp-tools-commands.interface';
import { selectMessageLogGroupsByMessageId, selectMessageLogDetailsByMessageId, selectRecurrenceInfoByMessageId } from "@common-reducers/CampaignSelectors";
import { useAppSelector, useAppDispatch } from "@common-reducers/hooks/store.hook";
import { MessageLogTimeline } from "./MessageLogTimeline";
import { MessageLogStatusBadge } from "./MessageLogStatusBadge";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import { ChevronDown, ChevronUp, Copy, Repeat, Calendar, Search } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useState, useMemo } from "react";
import moment from "moment";
import { MediaPreviewDialog } from "../../messages/components/MediaPreview";
import { AttachmentPreview } from "@/common/components/attachment-preview/attachment-preview";
import { blobMap } from "@common-services/blob-service";
import { handleMentionsFormatInMessage } from "@common-services/utils";
import { MessageStatusWithError } from "./MessageStatusWithError";
import { selectOnlineEngines } from "@common/reducers/ConnectedEnginesSlice";
import { UnloggedMessageKeyDialog } from "./UnloggedMessageKeyDialog";
import { selectUserRole } from "@common/reducers/UserSelectors";
import { waGetUnloggedWaMessageKeyIfExistInStoreTool } from "@/common/tools/whatsapp";

interface MessageLogCardProps {
    messageId?: string | string[]; // Support single messageId or array of messageIds
    onMediaClick?: (message: any) => void;
    message?: any; // Allow passing message object directly
}

function MessagePreview({ message }: { message: any }) {
    if (!message) return null;

    // Use message.message (not message.text) as that's the field name in UserMessage
    const messageText = message.message || '';
    const hasImage = message.imageId || message.asset?.url;

    return (
        <div className="space-y-3">
            {/* Message body */}
            {messageText ? (
                <div className="text-sm whitespace-pre-wrap break-words text-slate-900 dark:text-slate-100">
                    {handleMentionsFormatInMessage(messageText)}
                </div>
            ) : (
                <span className="text-slate-400 dark:text-slate-500 italic text-sm">No text content</span>
            )}
            
            {/* Image/Media preview */}
            {hasImage && (
                <div className="mt-2 max-w-md mx-auto rounded-lg overflow-hidden" style={{ height: '300px', maxHeight: '300px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <div className="w-full h-full relative rounded-lg overflow-hidden">
                        <AttachmentPreview
                            attachment={message.imageId ? blobMap.get(message.imageId) : null}
                            attachmentUrl={message.asset?.url}
                            attachmentType={message.asset?.type}
                            attachmentName={message.asset?.name}
                            previewMaxHeight="300px"
                            imageObjectFit="contain"
                        />
                    </div>
                </div>
            )}
        </div>
    );
}

function ContactDisplay({ contact }: { contact?: any }) {
    if (!contact?.id) return null;
    
    return (
        <div className="flex items-center gap-2 text-sm">
            <span className="text-slate-500 dark:text-slate-400">Contact:</span>
            <code className="text-xs bg-slate-100 dark:bg-slate-700 px-2 py-1 rounded font-mono text-slate-700 dark:text-slate-200">
                {contact.name ? `${contact.name} (${contact.id})` : contact.id}
            </code>
        </div>
    );
}

export function MessageLogCard({ messageId, onMediaClick, message: providedMessage }: MessageLogCardProps) {
    const [isExpanded, setIsExpanded] = useState(false);
    const [selectedMedia, setSelectedMedia] = useState<any | null>(null);
    const [copiedId, setCopiedId] = useState<string | null>(null);
    const [investigationDialogOpen, setInvestigationDialogOpen] = useState(false);
    const [investigationResults, setInvestigationResults] = useState<Array<{
        engineId: string;
        engineName: string;
        status: 'loading' | 'success' | 'error';
        data?: {
            waMessageKey: string | null;
            debugInfo?: {
                totalMessagesFetched: number;
                top5Messages: Array<{ id: string; body: string; caption: string }>;
            };
        };
        error?: string;
        errorType?: string;
        errorCode?: string;
        errorMessage?: string;
        errorData?: any;
        errorStack?: string;
        errorSource?: string;
    }>>([]);
    const [isInvestigating, setIsInvestigating] = useState(false);

    const onlineEngines = useAppSelector(selectOnlineEngines);
    const userRole = useAppSelector(selectUserRole);
    const dispatch = useAppDispatch();

    // Normalize messageId to array format
    const messageIds = Array.isArray(messageId) ? messageId : (messageId ? [messageId] : []);
    const primaryMessageId = Array.isArray(messageId) ? messageId[0] : messageId;

    // Get grouped logs for this/these messageId(s)
    const logGroups = useAppSelector(state => 
        selectMessageLogGroupsByMessageId(state, messageIds.length > 0 ? messageIds : primaryMessageId || '')
    );

    // Get message details for this/these messageId(s)
    const selectorMessageDetails = useAppSelector(state => 
        selectMessageLogDetailsByMessageId(state, messageIds.length > 0 ? messageIds : primaryMessageId || '')
    );

    // Get recurrence info using selector
    const recurrenceInfo = useAppSelector(state => 
        selectRecurrenceInfoByMessageId(state, messageIds.length > 0 ? messageIds : primaryMessageId || '')
    );

    // Use provided message or fallback to selector
    // The selector returns an object { message: ... }, so we handle that structure
    const effectiveMessage = providedMessage || selectorMessageDetails?.message;

    if (!effectiveMessage || !primaryMessageId) return null;

    // Flatten grouped logs for calculations
    const logs = logGroups.flat();
    const message = effectiveMessage;
    // Fallback date if messageDetails from selector is not available
    const latestUpdate = selectorMessageDetails?.latestUpdate || message.updatedAt || message.createdAt || new Date().toISOString(); 
    const contact = message?.contactList?.[0];

    // Calculate attempts based on retryCount (not log count)
    // retryCount = 0 means 1 attempt (initial), retryCount = 1 means 2 attempts, etc.
    const retryCount = message?.retryCount ?? 0;
    const totalAttempts = retryCount + 1;

    // Count successful and failed attempts
    // Multiple logs can belong to the same attempt (e.g., before-wa-send + confirmed = 1 successful attempt)
    // Group by attemptType and determine final outcome per attempt
    const attemptResults = useMemo(() => {
        // Group logs by attemptType
        const logsByAttemptType = new Map<string, typeof logs>();
        
        logs.forEach(log => {
            const attemptType = log.metadata?.attemptType || 'initial';
            if (!logsByAttemptType.has(attemptType)) {
                logsByAttemptType.set(attemptType, []);
            }
            logsByAttemptType.get(attemptType)!.push(log);
        });
        
        let successCount = 0;
        let failureCount = 0;
        
        // For each attempt type, determine if it succeeded or failed
        logsByAttemptType.forEach((attemptLogs, attemptType) => {
            // Sort by timestamp to get chronological order
            const sortedLogs = [...attemptLogs].sort((a, b) => 
                moment(a.createdAt).valueOf() - moment(b.createdAt).valueOf()
            );
            
            // Check the final status of this attempt
            const finalLog = sortedLogs[sortedLogs.length - 1];
            if (finalLog) {
                if (['confirmed', 'received', 'read', 'bt-sent', 'played'].includes(finalLog.status)) {
                    successCount++;
                } else if (finalLog.status === 'failed') {
                    failureCount++;
                }
            }
        });
        
        return { successCount, failureCount };
    }, [logs]);

    const handleCopyId = async (e: React.MouseEvent, id: string) => {
        e.stopPropagation();
        await navigator.clipboard.writeText(id);
        setCopiedId(id);
        setTimeout(() => setCopiedId(null), 2000);
    };

    const handleMediaClick = (msg: any) => {
        if (onMediaClick) {
            onMediaClick(msg);
        } else {
            setSelectedMedia(msg);
        }
    };

    const handleInvestigateUnloggedMessage = async () => {
        if (!message || !contact?.id) return;

        // The engines that serve the store lookup (their presence `commands`).
        const availableEngines = onlineEngines.filter((e) =>
            e.commands.includes(WA_TOOL_COMMANDS.GET_UNLOGGED_WA_MESSAGE_KEY_IF_EXIST_IN_STORE));

        if (availableEngines.length === 0) {
            alert('No WhatsApp engine online');
            return;
        }

        // Clear previous results before starting new investigation
        setInvestigationResults([]);
        setIsInvestigating(true);
        setInvestigationDialogOpen(true);
        
        // Set initial loading state for all engines
        setInvestigationResults(availableEngines.map(engine => ({
            engineId: engine.id,
            engineName: engine.engineKind,
            status: 'loading' as const
        })));

        // Call investigation on each engine in parallel
        const results = await Promise.allSettled(
            availableEngines.map(async (engine) => {
                try {
                    // Use dueDate with 20 minute buffer (like in retrySendMessageThunk).
                    // Contract requires a Date instance (the page handler
                    // ingests a serialised dueDate downstream).
                    const investigationDueDate = message.dueDate
                        ? new Date(new Date(message.dueDate).getTime() - 20 * 60 * 1000)
                        : new Date();

                    const result = await waGetUnloggedWaMessageKeyIfExistInStoreTool({
                        chatId: contact.id,
                        userMessageId: primaryMessageId,
                        dueDate: investigationDueDate
                    });

                    // Thunk returns `WaToolResponse<unknown>`; investigation
                    // panel renders the page-handler success payload directly.
                    const data = result.success
                        ? (result.data as { waMessageKey: string | null; debugInfo?: { totalMessagesFetched: number; top5Messages: Array<{ id: string; body: string; caption: string }> } })
                        : undefined;
                    return {
                        engineId: engine.id,
                        engineName: engine.engineKind,
                        status: 'success' as const,
                        data,
                    };
                } catch (error: any) {
                    return {
                        engineId: engine.id,
                        engineName: engine.engineKind,
                        status: 'error' as const,
                        error: error?.message || error?.data?.error || 'Unknown error',
                        errorType: error?.data?.errorType,
                        errorCode: error?.data?.errorCode,
                        errorMessage: error?.data?.errorMessage,
                        errorData: error?.data?.errorData,
                        errorStack: error?.data?.errorStack,
                        errorSource: error?.data?.errorSource
                    };
                }
            })
        );

        const finalResults = results.map((result, index) => {
            if (result.status === 'fulfilled') {
                return result.value;
            } else {
                const error = result.reason;
                return {
                    engineId: availableEngines[index].id,
                    engineName: availableEngines[index].engineKind,
                    status: 'error' as const,
                    error: error?.message || error?.data?.error || 'Unknown error',
                    errorType: error?.data?.errorType,
                    errorCode: error?.data?.errorCode,
                    errorMessage: error?.data?.errorMessage,
                    errorData: error?.data?.errorData,
                    errorStack: error?.data?.errorStack,
                    errorSource: error?.data?.errorSource
                };
            }
        });

        setInvestigationResults(finalResults);
        setIsInvestigating(false);
    };

    const handleDialogClose = (open: boolean) => {
        setInvestigationDialogOpen(open);
        // Clear results when dialog is closed
        if (!open) {
            setInvestigationResults([]);
            setIsInvestigating(false);
        }
    };

    return (
        <>
            <Card className="overflow-hidden border-0 shadow-none">
                {/* Top Summary Header - Quick overview stats */}
                <CardHeader className="pb-3 bg-slate-50 dark:bg-slate-800/50">
                    <div className="flex items-start justify-between gap-4">
                        <div className="flex-1 min-w-0">
                            {/* Status and Summary Stats Row */}
                            <div className="flex items-center gap-4 flex-wrap mb-3">
                                <MessageStatusWithError messageId={primaryMessageId} />
                                {messageIds.length > 1 && (
                                    <Badge variant="outline" className="bg-blue-50 dark:bg-blue-900/20 text-blue-700 dark:text-blue-400 border-blue-200 dark:border-blue-800">
                                        {messageIds.length} Messages
                                    </Badge>
                                )}
                                <div className="flex flex-wrap items-center gap-4 text-sm">
                                    <div className="text-slate-700 dark:text-slate-300">
                                        <span className="font-medium">Attempts: </span>
                                        <span>{totalAttempts}</span>
                                    </div>
                                    {attemptResults.successCount > 0 && (
                                        <div className="text-slate-700 dark:text-slate-300">
                                            <span className="font-medium">Success: </span>
                                            <span className="text-green-600 dark:text-green-400 font-semibold">{attemptResults.successCount}</span>
                                        </div>
                                    )}
                                    {attemptResults.failureCount > 0 && (
                                        <div className="text-slate-700 dark:text-slate-300">
                                            <span className="font-medium">Failures: </span>
                                            <span className="text-red-600 dark:text-red-400 font-semibold">{attemptResults.failureCount}</span>
                                        </div>
                                    )}
                                    <div className="text-slate-500 dark:text-slate-400 text-xs">
                                        <span className="font-medium">Latest: </span>
                                        <span>{moment(latestUpdate).format('MM/DD/YYYY, h:mm:ss A')}</span>
                                    </div>
                                </div>
                            </div>
                            
                            {/* Message Content */}
                            <MessagePreview message={message} />
                            
                            {/* Contact and Campaign Info */}
                            <div className="flex flex-wrap items-center gap-4 mt-2 text-sm text-slate-600 dark:text-slate-300">
                                <ContactDisplay contact={contact} />
                                {message?.isRecurring && (
                                    <Badge variant="outline" className="bg-purple-50 dark:bg-purple-900/20 text-purple-700 dark:text-purple-400 border-purple-200 dark:border-purple-800 flex items-center gap-1">
                                        <Repeat className="h-3 w-3" />
                                        Recurring
                                    </Badge>
                                )}
                                {message?.campaignId && (
                                    <>
                                        <Badge variant="outline" className="bg-slate-100 dark:bg-slate-700 text-slate-700 dark:text-slate-200 border-slate-300 dark:border-slate-600">
                                            Campaign
                                        </Badge>
                                        <div className="flex items-center gap-2">
                                            <span className="text-slate-500 dark:text-slate-400">Campaign ID:</span>
                                            <code className="text-xs bg-slate-100 dark:bg-slate-700 px-2 py-1 rounded font-mono text-slate-700 dark:text-slate-200">
                                                {message.campaignId}
                                            </code>
                                        </div>
                                    </>
                                )}
                            </div>

                            {/* Recurrence Schedule Explanation */}
                            {recurrenceInfo && (
                                <div className="mt-3 p-3 bg-purple-50/50 dark:bg-purple-900/10 border border-purple-200 dark:border-purple-800 rounded-md">
                                    <div className="flex items-start gap-2">
                                        <Repeat className="h-4 w-4 text-purple-600 dark:text-purple-400 mt-0.5 flex-shrink-0" />
                                        <div className="flex-1 min-w-0">
                                            <div className="text-sm font-medium text-purple-900 dark:text-purple-200 mb-1">
                                                Recurrence Schedule
                                            </div>
                                            <div className="text-sm text-purple-800 dark:text-purple-300 mb-2">
                                                {recurrenceInfo.recurrenceText}
                                            </div>
                                            {recurrenceInfo.nextDueDate && (
                                                <div className="flex items-center gap-2 text-xs text-purple-700 dark:text-purple-400 mt-2 pt-2 border-t border-purple-200 dark:border-purple-700">
                                                    <Calendar className="h-3 w-3" />
                                                    <span className="font-medium">Next due date:</span>
                                                    <span>{recurrenceInfo.nextDueDate.format('DD/MM/YYYY, h:mm:ss A')}</span>
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            )}
                        </div>
                        <div className="flex items-center gap-2 shrink-0">
                            <Button
                                variant="ghost"
                                size="sm"
                                onClick={(e) => {
                                    e.stopPropagation();
                                    handleInvestigateUnloggedMessage();
                                }}
                                className="h-8 px-2"
                                title="Investigate unlogged message key"
                            >
                                <Search className="h-4 w-4 text-slate-600 dark:text-slate-400" />
                            </Button>
                            {userRole === 'admin' && (
                                <Button
                                    variant="ghost"
                                    size="sm"
                                    onClick={() => handleCopyId({ stopPropagation: () => {} } as any, primaryMessageId)}
                                    className="h-8 w-8 p-0"
                                >
                                    <Copy className={copiedId === primaryMessageId ? "h-4 w-4 text-green-600 dark:text-green-400" : "h-4 w-4 text-slate-600 dark:text-slate-400"} />
                                </Button>
                            )}
                        </div>
                    </div>
                </CardHeader>

                <Separator className="bg-slate-200 dark:bg-slate-700" />

                {/* Timeline Section - Detailed event log */}
                <CardContent className="pt-4">
                    <div className="mb-4">
                        <div className="flex items-center justify-between mb-3">
                            <h4 className="text-sm font-semibold text-slate-900 dark:text-slate-100">Timeline</h4>
                            <span className="text-xs text-slate-500 dark:text-slate-400">
                                {logs.length} log{logs.length !== 1 ? 's' : ''}
                            </span>
                        </div>
                        {logs.length > 0 && logGroups.length > 0 ? (
                            <>
                                <MessageLogTimeline logs={logGroups} messageId={primaryMessageId} dueDate={message?.dueDate} />
                                {/* Debug info - can be removed later */}
                                {process.env.NODE_ENV === 'development' && (
                                    <details className="mt-4 text-xs text-slate-400 dark:text-slate-500">
                                        <summary className="cursor-pointer">Debug: Show all log details</summary>
                                        <pre className="mt-2 p-2 bg-slate-50 dark:bg-slate-800 rounded overflow-auto max-h-40 text-[10px] text-slate-900 dark:text-slate-200">
                                            {JSON.stringify(logs.map(log => ({
                                                _id: log._id,
                                                status: log.status,
                                                attemptType: log.metadata?.attemptType,
                                                whatsappStatus: log.metadata?.whatsappStatus,
                                                waVersion: log.metadata?.waVersion,
                                                waMeUser: log.metadata?.waMeUser,
                                                createdAt: log.createdAt,
                                                waMessageKey: log.waMessageKey,
                                                error: log.error
                                            })), null, 2)}
                                        </pre>
                                    </details>
                                )}
                            </>
                        ) : (
                            <p className="text-sm text-slate-500 dark:text-slate-400">No log entries available</p>
                        )}
                    </div>
                </CardContent>

                {/* Footer - Full message JSON details (collapsed by default) */}
                <Separator className="bg-slate-200 dark:bg-slate-700" />
                <CardFooter className="pt-3 pb-3">
                    <Collapsible open={isExpanded} onOpenChange={setIsExpanded}>
                        <CollapsibleTrigger asChild>
                            <Button variant="ghost" size="sm" className="text-xs text-slate-500 dark:text-slate-400 hover:text-slate-700 dark:hover:text-slate-200">
                                {isExpanded ? (
                                    <>
                                        <ChevronUp className="h-3 w-3 mr-1" />
                                        Hide Details
                                    </>
                                ) : (
                                    <>
                                        <ChevronDown className="h-3 w-3 mr-1" />
                                        Show Details
                                    </>
                                )}
                            </Button>
                        </CollapsibleTrigger>
                        <CollapsibleContent className="w-full mt-2">
                            <div className="space-y-2 text-sm text-slate-900 dark:text-slate-100">
                                <Separator className="bg-slate-200 dark:bg-slate-700" />
                                <div>
                                    <strong>Message ID{messageIds.length > 1 ? 's' : ''}:</strong>
                                    {messageIds.length > 1 ? (
                                        <div className="mt-1 space-y-1">
                                            {messageIds.map((id, idx) => (
                                                <div key={idx} className="flex items-center gap-2">
                                                    <code className="flex-1 text-xs bg-slate-100 dark:bg-slate-700 px-2 py-1 rounded font-mono text-slate-700 dark:text-slate-200">
                                                        {id}
                                                    </code>
                                                    <Button
                                                        variant="ghost"
                                                        size="sm"
                                                        className="h-6 w-6 p-0"
                                                        onClick={(e) => {
                                                            e.stopPropagation();
                                                            handleCopyId(e as any, id);
                                                        }}
                                                    >
                                                        <Copy className={copiedId === id ? "h-3 w-3 text-green-600 dark:text-green-400" : "h-3 w-3 text-slate-600 dark:text-slate-400"} />
                                                    </Button>
                                                </div>
                                            ))}
                                        </div>
                                    ) : (
                                        <div className="flex items-center gap-2 mt-1">
                                            <code className="flex-1 text-xs bg-slate-100 dark:bg-slate-700 px-2 py-1 rounded font-mono text-slate-700 dark:text-slate-200">
                                                {primaryMessageId}
                                            </code>
                                            <Button
                                                variant="ghost"
                                                size="sm"
                                                className="h-6 w-6 p-0"
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    handleCopyId(e as any, primaryMessageId);
                                                }}
                                            >
                                                <Copy className={copiedId === primaryMessageId ? "h-3 w-3 text-green-600 dark:text-green-400" : "h-3 w-3 text-slate-600 dark:text-slate-400"} />
                                            </Button>
                                        </div>
                                    )}
                                </div>
                                {message?.dueDate && (
                                    <div>
                                        <strong>Due Date:</strong>
                                        <span className="ml-2">{moment(message.dueDate).format('MM/DD/YYYY, h:mm:ss A')}</span>
                                    </div>
                                )}
                                <div>
                                    <strong>Full Message:</strong>
                                    <pre className="mt-1 p-2 bg-slate-50 dark:bg-slate-800 rounded text-xs overflow-auto max-h-40 text-slate-900 dark:text-slate-200">
                                        {JSON.stringify(message, null, 2)}
                                    </pre>
                                </div>
                            </div>
                        </CollapsibleContent>
                    </Collapsible>
                </CardFooter>
            </Card>

            <MediaPreviewDialog 
                open={!!selectedMedia}
                onClose={() => setSelectedMedia(null)}
                message={selectedMedia}
            />

            <UnloggedMessageKeyDialog
                open={investigationDialogOpen}
                onOpenChange={handleDialogClose}
                results={investigationResults}
                isRunning={isInvestigating}
            />
        </>
    );
}

